"use client"

import { useAuth } from '@/lib/profile-context'
import {
    DEFAULT_ROLE, RolePermissionManager,
    RolePermissions as BaseRolePermissions, UserRole
} from "@/lib/permissions/role-permissions"
import { useMemo } from 'react'

interface EquipeInfo {
  id: string
  nome: string
  codigo: string
  is_suporte: boolean
}

interface RolePermissions extends BaseRolePermissions {
  role: UserRole | null
  loading: boolean
  equipe: EquipeInfo | null
  equipeIsSuporte: boolean  // Flag direta para verificar se equipe é de suporte
  isAprovador: boolean      // Flag para usuários aprovadores de tickets
}

export function useRoleAccess(): RolePermissions {
  const authContext = useAuth()

  // Safe destructuring - protege contra undefined
  const { user = null, profile = null, loading: authLoading = false } = authContext || {}

  const permissions = useMemo(() => {
    // Se ainda está carregando a autenticação, retornar loading
    if (authLoading) {
      return {
        ...RolePermissionManager.getPermissions(DEFAULT_ROLE),
        role: null,
        loading: true,
        equipe: null,
        equipeIsSuporte: false,
        isAprovador: false,
      }
    }

    // Se não há usuário logado, retornar sem loading
    if (!user) {
      return {
        ...RolePermissionManager.getPermissions(DEFAULT_ROLE),
        role: null,
        loading: false,
        equipe: null,
        equipeIsSuporte: false,
        isAprovador: false,
      }
    }

    // Se há usuário mas não há profile, usar permissões padrão
    if (!profile) {
      return {
        ...RolePermissionManager.getPermissions(DEFAULT_ROLE),
        role: DEFAULT_ROLE,
        loading: false,
        equipe: null,
        equipeIsSuporte: false,
        isAprovador: false,
      }
    }

    // Buscar permissões baseadas no role do profile
    const userRole = profile.role as UserRole

    // Validar se o role é válido
    if (!RolePermissionManager.isValidRole(userRole)) {
      console.warn('⚠️ [ROLE-ACCESS] Role inválido detectado:', userRole, '- usando padrão')
      return {
        ...RolePermissionManager.getPermissions(DEFAULT_ROLE),
        role: DEFAULT_ROLE,
        loading: false,
        equipe: null,
        equipeIsSuporte: false,
        isAprovador: false,
      }
    }

    const rolePermissions = RolePermissionManager.getPermissions(userRole)

    // Extrair dados da equipe do profile
    const userEquipe = (profile as any).equipe as EquipeInfo | null
    const equipeIsSuporte = userEquipe?.is_suporte === true
    const isAprovador = (profile as any).is_aprovador === true

    const finalPermissions = {
      ...rolePermissions,
      role: userRole,
      loading: false,
      equipe: userEquipe,
      equipeIsSuporte,
      isAprovador,
    }

    return finalPermissions
  }, [user, profile, authLoading])

  return permissions
}

// Hook para verificar acesso a uma rota específica
export function useRouteAccess(route: string): boolean {
  const permissions = useRoleAccess()

  if (permissions.loading) {
    return false // Bloquear acesso enquanto carrega
  }

  if (!permissions.role) {
    return false // Usuário não autenticado
  }

  // Bypass: aprovadores sempre acessam rotas de tickets (fila + dashboard)
  // independentemente do role, pois precisam validar chamados pendentes
  if (permissions.isAprovador && route.startsWith('/backoffice/tickets')) {
    return true
  }

  return RolePermissionManager.canAccessRoute(permissions.role, route)
}

// Hook para obter informações do role atual
export function useRoleInfo() {
  const permissions = useRoleAccess()
  
  return {
    role: permissions.role,
    displayName: permissions.role ? RolePermissionManager.getRoleDisplayName(permissions.role) : null,
    allowedRoutes: permissions.role ? RolePermissionManager.getAllowedRoutes(permissions.role) : [],
    isAdmin: permissions.role ? ['gestor_backoffice', 'gestor_comercial', 'adm_master'].includes(permissions.role) : false,
    isManager: permissions.role ? ['gestor_backoffice', 'gestor_comercial'].includes(permissions.role) : false,
    loading: permissions.loading
  }
}

// Hook para redirecionamento baseado em role
export function useRoleRedirect(): string {
  const { role } = useRoleInfo()
  const permissions = useRoleAccess()

  if (!role) {
    return '/login'
  }

  // Para usuários com roles de TI (it ou gestor_it), redirecionar para /backoffice/tickets
  const isITRole = role === 'it' || role === 'gestor_it'
  if (isITRole) {
    return '/backoffice/tickets'
  }

  // Obter primeira rota permitida como padrão de redirecionamento
  const allowedRoutes = RolePermissionManager.getAllowedRoutes(role)
  return allowedRoutes.length > 0 ? allowedRoutes[0] : '/inicio'
}

export default useRoleAccess